import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { FilterIcon, SearchIcon, BookOpenIcon, UsersIcon, ShoppingBagIcon, HeadphonesIcon, LibraryIcon, LayoutGridIcon } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { usePlatform } from '../context/PlatformContext';
import { useAfterTideFilter } from '../hooks/useAfterTideFilter';
import UnifiedBookCard from '../components/Books/UnifiedBookCard';
import { allBooks } from '../data/booksData';
import { aftertideBooks } from '../data/aftertideBooksData';
import { ProfileCard } from '../components/Directory/ProfileCard';
import ProductCard from '../components/Products/ProductCard';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../components/UI/FilterSidebar';
import ViewModeToggle from '../components/UI/ViewModeToggle';
import { mapAuthorToProfileCard } from '../utils/profileCardMappers';
// Mock Data
const mockBooks = [{
  id: '1',
  title: 'The Silent Echo',
  author: 'Elena Richards',
  coverImage: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.7,
  genres: ['Thriller', 'Mystery'],
  price: 14.99,
  format: 'paperback'
}, {
  id: '2',
  title: 'Heart of the Storm',
  author: 'Marcus Chen',
  coverImage: 'https://images.unsplash.com/photo-1516979187457-637abb4f9353?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.5,
  genres: ['Fantasy', 'Romance'],
  price: 16.99,
  format: 'hardcover'
}, {
  id: '3',
  title: 'Midnight Whispers',
  author: 'Sophia Williams',
  coverImage: 'https://images.unsplash.com/photo-1633477189729-9290b3261d0a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.2,
  genres: ['Horror', 'Supernatural'],
  price: 9.99,
  format: 'ebook'
}, {
  id: '4',
  title: 'The Last Starship',
  author: 'Alex Zhang',
  coverImage: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  rating: 4.8,
  genres: ['Sci-Fi', 'Space Opera'],
  price: 12.99,
  format: 'audiobook'
}];
const mockAuthors = [{
  slug: 'elena-richards',
  name: 'Elena Richards',
  avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Award-winning author of psychological thrillers.',
  location: 'Seattle, WA',
  genres: ['Thriller', 'Mystery'],
  bookCount: 12,
  rating: 4.7
}, {
  slug: 'marcus-chen',
  name: 'Marcus Chen',
  avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Fantasy author creating immersive worlds.',
  location: 'Portland, OR',
  genres: ['Fantasy', 'YA'],
  bookCount: 8,
  rating: 4.5
}];
const mockMerch = [{
  id: '1',
  title: 'Silent Echo T-Shirt',
  vendor: 'Literary Apparel Co.',
  image: 'https://images.unsplash.com/photo-1576566588028-4147f3842f27?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: 24.99,
  relatedBook: 'The Silent Echo',
  isOfficialMerch: true
}, {
  id: '2',
  title: 'Fantasy Map Mug',
  vendor: 'Cosmic Creations',
  image: 'https://images.unsplash.com/photo-1514228742587-6b1558fcca3d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: 18.5,
  relatedBook: 'Heart of the Storm',
  isOfficialMerch: false
}];
type TabType = 'all' | 'books' | 'authors' | 'merch' | 'audiobooks' | 'series';
export default function SearchResults() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    isAfterTide
  } = usePlatform();
  const {
    platformData
  } = useAfterTideFilter();
  // Use platform-appropriate data
  const baseBooks = platformData(allBooks, aftertideBooks);
  const [activeTab, setActiveTab] = useState('books');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Filter states
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState<string>('all');
  const [selectedRating, setSelectedRating] = useState<string>('all');
  const [sortBy, setSortBy] = useState('relevance');
  // Sync tab with URL
  useEffect(() => {
    if (activeTab !== 'books') {
      setActiveTab('books');
    }
  }, []);
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
  };
  // Filter Configuration
  const filterSections: FilterSection[] = [{
    id: 'sort',
    title: 'Sort By',
    type: 'radio',
    options: [{
      value: 'relevance',
      label: 'Relevance'
    }, {
      value: 'newest',
      label: 'Newest'
    }, {
      value: 'price-low',
      label: 'Price: Low to High'
    }, {
      value: 'price-high',
      label: 'Price: High to Low'
    }, {
      value: 'rating',
      label: 'Rating'
    }],
    value: sortBy,
    onChange: setSortBy
  }, {
    id: 'genre',
    title: 'Genre',
    type: 'checkbox',
    options: [{
      value: 'romance',
      label: 'Romance'
    }, {
      value: 'fantasy',
      label: 'Fantasy'
    }, {
      value: 'thriller',
      label: 'Thriller'
    }, {
      value: 'sci-fi',
      label: 'Sci-Fi'
    }, {
      value: 'mystery',
      label: 'Mystery'
    }, {
      value: 'horror',
      label: 'Horror'
    }],
    value: selectedGenres,
    onChange: val => {
      setSelectedGenres(prev => prev.includes(val) ? prev.filter(g => g !== val) : [...prev, val]);
    }
  }, {
    id: 'price',
    title: 'Price Range',
    type: 'radio',
    options: [{
      value: 'all',
      label: 'All Prices'
    }, {
      value: 'under-5',
      label: 'Under $5'
    }, {
      value: '5-10',
      label: '$5 - $10'
    }, {
      value: '10-20',
      label: '$10 - $20'
    }, {
      value: 'over-20',
      label: 'Over $20'
    }],
    value: priceRange,
    onChange: setPriceRange
  }, {
    id: 'rating',
    title: 'Rating',
    type: 'radio',
    options: [{
      value: 'all',
      label: 'All Ratings'
    }, {
      value: '4-plus',
      label: '4+ Stars'
    }, {
      value: '3-plus',
      label: '3+ Stars'
    }],
    value: selectedRating,
    onChange: setSelectedRating
  }];
  const handleResetFilters = () => {
    setSelectedGenres([]);
    setPriceRange('all');
    setSelectedRating('all');
    setSortBy('relevance');
  };
  // Render Helpers
  const renderBooks = (limit?: number) => {
    const books = limit ? baseBooks.slice(0, limit) : baseBooks;
    return <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4' : 'grid-cols-1'}`} data-id="element-12667" data-mp="2j6ecwnq33mlk">
        {books.map(book => <UnifiedBookCard key={book.id} book={book} data-id="element-12668" data-mp="2t4an9db76dim" />)}
      </div>;
  };
  const renderAuthors = (limit?: number) => {
    const authors = limit ? mockAuthors.slice(0, limit) : mockAuthors;
    return <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`} data-id="element-12669" data-mp="8ebfp4y2e6gj">
        {authors.map(author => <ProfileCard key={author.slug} data={mapAuthorToProfileCard(author)} viewMode={viewMode} data-id="element-12670" data-mp="yfdua45pcwlu" />)}
      </div>;
  };
  const renderMerch = (limit?: number) => {
    const merch = limit ? mockMerch.slice(0, limit) : mockMerch;
    return <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4' : 'grid-cols-1'}`} data-id="element-12671" data-mp="13cw4dxvghhvf">
        {merch.map(item => <ProductCard key={item.id} {...item} data-id="element-12672" data-mp="1d2ywzwyo0tt8" />)}
      </div>;
  };
  const tabs = [{
    id: 'all',
    label: 'All',
    icon: LayoutGridIcon
  }, {
    id: 'books',
    label: 'Books',
    icon: BookOpenIcon
  }, {
    id: 'authors',
    label: 'Authors',
    icon: UsersIcon
  }, {
    id: 'merch',
    label: 'Merch',
    icon: ShoppingBagIcon
  }, {
    id: 'audiobooks',
    label: 'Audiobooks',
    icon: HeadphonesIcon
  }, {
    id: 'series',
    label: 'Series',
    icon: LibraryIcon
  }];
  return <div className={`min-h-screen ${dk.page} pb-12`} data-id="element-12673" data-mp="28bspc93brprk">
      {/* Header */}
      <div className={`${dk.surface} border-b ${dk.border} pt-8 pb-0`} data-id="element-12674" data-mp="1tor7o5vb6x2f">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-12675" data-mp="1ynatm69wk8us">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6" data-id="element-12676" data-mp="1ylpvy7sak9sp">
            <div data-id="element-12677" data-mp="1klyge2m4mlcl">
              <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-12678" data-mp="1kpm9x1rkn7rb">
                Search Results
              </h1>
              <p className={`mt-1 ${dk.textSecondary}`} data-id="element-12679" data-mp="1apdazg673888">
                {baseBooks.length + mockAuthors.length + mockMerch.length}{' '}
                results for <span className={dk.textPrimary} data-id="element-12680" data-mp="1fp829ktvtztt">"{query}"</span>
              </p>
            </div>
            <div className="flex items-center gap-3" data-id="element-12681" data-mp="354o40f5h8j9o">
              <div className="lg:hidden" data-id="element-12682" data-mp="3a27jfidwv237">
                <button onClick={() => setSidebarOpen(true)} className={`flex items-center px-4 py-2 rounded-lg border ${dk.border} ${dk.surface} ${dk.textPrimary}`} data-id="element-12683" data-mp="3a166179fa3ib">
                  <FilterIcon className="h-4 w-4 mr-2" data-id="element-12684" data-mp="1vxpwtjyvlbvi" />
                  Filters
                </button>
              </div>
              <ViewModeToggle viewMode={viewMode} setViewMode={setViewMode} data-id="element-12685" data-mp="1m5tozh6zm7e3" />
            </div>
          </div>

          {/* Tabs */}
          <div className="flex overflow-x-auto hide-scrollbar gap-6" data-id="element-12686" data-mp="29wftwzlp3bjv">
            {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => handleTabChange(tab.id as TabType)} className={`flex items-center gap-2 pb-4 border-b-2 transition-colors whitespace-nowrap ${isActive ? `${rt.borderActive} ${rt.textPrimary}` : `border-transparent ${dk.textMuted} hover:${dk.textPrimary}`}`} data-id="element-12687" data-mp="1zwoy8uleeqi7">
                  <Icon className="h-4 w-4" data-id="element-12688" data-mp="37o4ea8os3xon" />
                  <span className="font-medium" data-id="element-12689" data-mp="37n2spaa4zsso">{tab.label}</span>
                </button>;
          })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-12690" data-mp="3gpvjlcfeq2hz">
        <div className="flex gap-8" data-id="element-12691" data-mp="3co0gq3htvh23">
          {/* Sidebar */}
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-12692" data-mp="3cos26lhpnyen">
            <FilterSidebar sections={filterSections} onReset={handleResetFilters} data-id="element-12693" data-mp="2xr9gjs0by1bf" />
          </FilterSidebarWrapper>

          {/* Main Content */}
          <main className="flex-1 min-w-0" data-id="element-12694" data-mp="25af580r8tmmk">
            {activeTab === 'all' ? <div className="space-y-12" data-id="element-12695" data-mp="2582ilaajio36">
                {/* Books Section */}
                <section data-id="element-12696" data-mp="218lccenahmwy">
                  <div className="flex items-center justify-between mb-4" data-id="element-12697" data-mp="487ap67pra77">
                    <h2 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-12698" data-mp="464oyydlg38a">
                      Books
                    </h2>
                    <button onClick={() => handleTabChange('books')} className={`text-sm font-medium ${rt.accentLink}`} data-id="element-12699" data-mp="dyae80cgo6js">
                      View All Books
                    </button>
                  </div>
                  {renderBooks(4)}
                </section>

                {/* Authors Section */}
                <section data-id="element-12700" data-mp="3mfwurbmwh3zv">
                  <div className="flex items-center justify-between mb-4" data-id="element-12701" data-mp="3rd6ov4l8i3u1">
                    <h2 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-12702" data-mp="3rf9q6kru7je2">
                      Authors
                    </h2>
                    <button onClick={() => handleTabChange('authors')} className={`text-sm font-medium ${rt.accentLink}`} data-id="element-12703" data-mp="16n7lz9j1qkya">
                      View All Authors
                    </button>
                  </div>
                  {renderAuthors(3)}
                </section>

                {/* Merch Section */}
                <section data-id="element-12704" data-mp="2me0bz08rfepk">
                  <div className="flex items-center justify-between mb-4" data-id="element-12705" data-mp="2md8ibuyq3r1x">
                    <h2 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-12706" data-mp="2hf6yv8xdxppx">
                      Merchandise
                    </h2>
                    <button onClick={() => handleTabChange('merch')} className={`text-sm font-medium ${rt.accentLink}`} data-id="element-12707" data-mp="2cgvn2snj5552">
                      View All Merch
                    </button>
                  </div>
                  {renderMerch(4)}
                </section>
              </div> :
          // Specific Tab Content
          <div className="space-y-6" data-id="element-12708" data-mp="1mgg3jzwoky2">
                {activeTab === 'books' && renderBooks()}
                {activeTab === 'authors' && renderAuthors()}
                {activeTab === 'merch' && renderMerch()}
                {activeTab === 'audiobooks' && <div className={`text-center py-12 ${dk.card} rounded-lg`} data-id="element-12709" data-mp="2uiro1tk44dv5">
                    <HeadphonesIcon className={`h-12 w-12 mx-auto mb-4 ${dk.textMuted}`} data-id="element-12710" data-mp="2gigw0n72gt8e" />
                    <h3 className={`text-lg font-medium ${dk.textPrimary}`} data-id="element-12711" data-mp="2lgif384hssjv">
                      No audiobooks found
                    </h3>
                    <p className={dk.textSecondary} data-id="element-12712" data-mp="26qknei7ctkpx">
                      Try adjusting your search terms.
                    </p>
                  </div>}
                {activeTab === 'series' && <div className={`text-center py-12 ${dk.card} rounded-lg`} data-id="element-12713" data-mp="22r3fzj8dadi4">
                    <LibraryIcon className={`h-12 w-12 mx-auto mb-4 ${dk.textMuted}`} data-id="element-12714" data-mp="22ozef7xxxql0" />
                    <h3 className={`text-lg font-medium ${dk.textPrimary}`} data-id="element-12715" data-mp="1ssmkc8yhhapq">
                      No series found
                    </h3>
                    <p className={dk.textSecondary} data-id="element-12716" data-mp="197vtz00kdtxb">
                      Try adjusting your search terms.
                    </p>
                  </div>}
              </div>}
          </main>
        </div>
      </div>
    </div>;
}